import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClientProvider, QueryClient } from "@tanstack/react-query";
import { BrowserRouter, HashRouter, Routes, Route, Navigate } from "react-router-dom";
import { Layout } from "./components/Layout";
import Dashboard from "./pages/Dashboard";
import Candidates from "./pages/Candidates";
import Companies from "./pages/Companies";
import JobOffers from "./pages/JobOffers";
import Matches from "./pages/Matches";
import Kanban from "./pages/Kanban";
import Tasks from "./pages/Tasks";
import Calendar from "./pages/Calendar";
import CandidateDetail from "./pages/CandidateDetail";
import CompanyDetail from "./pages/CompanyDetail";
import JobDetail from "./pages/JobDetail";
import MatchDetail from "./pages/MatchDetail";
import NotFound from "./pages/NotFound";
import Auth from "./pages/Auth";
import AdminConsole from "./pages/AdminConsole";
import Settings from "./pages/Settings";
import MatchTest from "./pages/MatchTest";
import Onboarding from "./pages/Onboarding";
import ResetPasswordPage from "./pages/ResetPasswordPage";
import RegisterPage from "./pages/RegisterPage";
import { SettingsProvider } from "./providers/SettingsProvider";
import { AuthProvider } from "./providers/AuthProvider";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { OnboardingCheck } from "./components/OnboardingCheck";
import { DevModeProtectedRoute } from "./components/DevModeProtectedRoute";
import { useEffect } from 'react';
import { ensureJobsTableColumns } from './integrations/supabase/ensure-columns';

// Create a query client instance
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  useEffect(() => {
    // Ensure all required columns exist when the app loads
    ensureJobsTableColumns().catch(console.error);
  }, []);

  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <AuthProvider>
          <SettingsProvider>
            <TooltipProvider>
              <Toaster />
              <Sonner />
              <Routes>
                <Route path="/auth" element={<Auth />} />
                <Route path="/register" element={<RegisterPage />} />
                <Route path="/onboarding" element={<Onboarding />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                {<Route element={<ProtectedRoute />}>
                  <Route element={<OnboardingCheck>
                    <Layout />
                  </OnboardingCheck>}>
                    <Route index element={<Dashboard />} />
                    <Route path="candidates" element={<Candidates />} />
                    <Route path="candidates/:id" element={<CandidateDetail />} />
                    <Route path="companies" element={<Companies />} />
                    <Route path="companies/:id" element={<CompanyDetail />} />
                    <Route path="jobs" element={<JobOffers />} />
                    <Route path="jobs/:id" element={<JobDetail />} />
                    <Route path="matches" element={<Matches />} />
                    <Route path="matches/:id" element={<MatchDetail />} />
                    <Route path="kanban" element={<Kanban />} />
                    <Route path="tasks" element={<Tasks />} />
                    <Route path="calendar" element={<Calendar />} />
                    <Route path="match-test" element={
                      <DevModeProtectedRoute>
                        <MatchTest />
                      </DevModeProtectedRoute>
                    } />
                    <Route path="settings" element={<Settings />} />
                    <Route path="admin" element={
                      <ProtectedRoute requireAdmin>
                        <AdminConsole />
                      </ProtectedRoute>
                    } />
                    <Route path="*" element={<NotFound />} />
                  </Route>
                </Route>}
              </Routes>
            </TooltipProvider>
          </SettingsProvider>
        </AuthProvider>
      </BrowserRouter>
    </QueryClientProvider>
  );
}

export default App;
